import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCoursesAsync, deleteCourseAsync, clearError } from '../store/slices/coursesSlice';
import LoadingSpinner from './LoadingSpinner';
import './Students.css';
import './GroupForm.css';

const Courses: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { courses, pagination, loading, error } = useAppSelector((s) => s.courses);

  useEffect(() => {
    dispatch(fetchCoursesAsync({ page: pagination.currentPage, limit: pagination.itemsPerPage }));
  }, [dispatch]);

  const gotoPage = (page: number) => {
    dispatch(fetchCoursesAsync({ page, limit: pagination.itemsPerPage }));
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this course?')) return;
    await dispatch(deleteCourseAsync(id));
  };

  if (loading && courses.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Courses</h1>
          <p>Manage courses and chapters</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/courses/create')}>
          Create Course
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <div className="results-summary">
        <p>
          Showing {courses.length} of {pagination.totalItems} courses
        </p>
      </div>

      <div className="students-table-container courses-table">
        {courses.length === 0 ? (
          <div className="no-students">
            <p>No courses found.</p>
            <button 
              onClick={() => navigate('/courses/create')}
              className="btn-primary"
            >
              Create your first course
            </button>
          </div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Level</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.level || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon"
                        onClick={() => navigate(`/courses/edit/${c.id}`)}
                        data-tooltip="Edit course"
                      >
                        ✎
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleDelete(c.id)}
                        data-tooltip="Delete course"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button 
            disabled={pagination.currentPage <= 1 || loading}
            onClick={() => gotoPage(pagination.currentPage - 1)}
            className="btn-secondary"
          >
            Previous
          </button>
          <div className="pagination-info">
            <span>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
          </div>
          <button 
            disabled={pagination.currentPage >= pagination.totalPages || loading}
            onClick={() => gotoPage(pagination.currentPage + 1)}
            className="btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {loading && courses.length > 0 && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default Courses;
